import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

/**
 * Fires a SPA page_view to GA4 (via gtag) and a `page_view` event to GTM's
 * dataLayer on every React Router navigation. Skips the initial mount so we
 * don't double-count the page_view that GA4/GTM auto-fires on script load.
 */
export default function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    const page_path = pathname + search;
    const page_location = window.location.origin + page_path;
    const page_title = document.title;

    // GTM dataLayer push — handles GA4, Meta, etc. configured inside GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_path,
      page_location,
      page_title,
    });

    // Direct GA4 gtag (fires only if gtag.js is loaded outside of GTM)
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path,
        page_location,
        page_title,
      });
    }
  }, [pathname, search]);

  // Delegated global listener for tel: / mailto: clicks anywhere on the site
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href^='tel:'], a[href^='mailto:']") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      const isPhone = href.startsWith("tel:");
      const value = href.replace(/^tel:|^mailto:/, "");
      const location = anchor.closest("footer")
        ? "footer"
        : anchor.closest("header, nav")
          ? "navbar"
          : window.location.pathname;

      trackEvent(isPhone ? "phone_click" : "email_click", {
        contact_method: isPhone ? "phone" : "email",
        contact_value: value,
        link_text: (anchor.textContent || "").trim().slice(0, 80),
        link_location: location,
        page_path: window.location.pathname,
      });
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true } as EventListenerOptions);
  }, []);

  return null;
}
